import { useEffect, useState } from "react"
import { Check, Copy, Loader2, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { apiClient } from "@/lib/api"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  contentType: "title" | "description" | "amenities" | "seasonal"
  propertyId?: string
  currentSeason?: string | null
}

interface GeneratedVariant {
  id: string
  text: string
  rationale: string
  sources: string[]
  charCount: number
}

const typeLabels: Record<Props["contentType"], string> = {
  title: "Title Fix Studio",
  description: "Description Fix Studio",
  amenities: "Amenity Fix Studio",
  seasonal: "Seasonal Fix Studio",
}

const typeDescriptions: Record<Props["contentType"], string> = {
  title: "Generate tighter headline variants grounded in your live audit and review proof.",
  description: "Turn audit findings and guest evidence into stronger listing copy.",
  amenities: "Clarify practical detail so filters, trust, and booking confidence improve.",
  seasonal: "Refresh the listing story for the current or upcoming demand context.",
}

const loadingMessages = [
  "Cross-checking the listing audit…",
  "Pulling guest proof into the draft…",
  "Shaping channel-specific variants…",
  "Tightening the copy around current demand…",
]

function normalizeSeason(value: string, currentSeason?: string | null): string | null {
  if (!value || value === "current") return currentSeason?.toLowerCase() || null
  return value
}

function requestContentType(type: Props["contentType"]): "titles" | "descriptions" {
  return type === "title" ? "titles" : "descriptions"
}

function responseToVariants(
  type: Props["contentType"],
  response: Awaited<ReturnType<typeof apiClient.generateListingContent>>,
): GeneratedVariant[] {
  if (!response.ok) return []
  if (type === "title") {
    return (response.titles || []).map((item, index) => ({
      id: `title-${index}`,
      text: item.text,
      rationale: item.rationale,
      sources: item.sources || [],
      charCount: item.char_count,
    }))
  }

  return (response.descriptions || []).map((item, index) => ({
    id: `description-${index}`,
    text: item.text,
    rationale: item.rationale,
    sources: item.sources || [],
    charCount: item.char_count,
  }))
}

export function ContentGeneratorDrawer({
  open,
  onOpenChange,
  contentType,
  propertyId,
  currentSeason,
}: Props) {
  const [channel, setChannel] = useState<"airbnb" | "booking" | "direct">("airbnb")
  const [season, setSeason] = useState("current")
  const [generating, setGenerating] = useState(false)
  const [variants, setVariants] = useState<GeneratedVariant[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [loadingMsg, setLoadingMsg] = useState(0)
  const [error, setError] = useState("")
  const [modelUsed, setModelUsed] = useState("")

  async function handleGenerate() {
    if (!propertyId || generating) return
    setGenerating(true)
    setError("")
    setVariants([])
    setModelUsed("")

    let msgIndex = 0
    const interval = window.setInterval(() => {
      msgIndex = (msgIndex + 1) % loadingMessages.length
      setLoadingMsg(msgIndex)
    }, 1400)

    try {
      const response = await apiClient.generateListingContent(propertyId, {
        channel,
        target_season: contentType === "seasonal" ? normalizeSeason(season, currentSeason) : null,
        content_type: requestContentType(contentType),
      })

      if (!response.ok) {
        setError(response.error || "Stayframe could not generate listing content right now.")
        return
      }

      const nextVariants = responseToVariants(contentType, response)
      if (nextVariants.length === 0) {
        setError("Stayframe returned no variants for this request.")
        return
      }

      setVariants(nextVariants)
      setModelUsed(response.model_used || "")
    } catch {
      setError("Stayframe could not generate listing content right now.")
    } finally {
      window.clearInterval(interval)
      setGenerating(false)
    }
  }

  useEffect(() => {
    if (!open) {
      setVariants([])
      setError("")
      setModelUsed("")
      setCopiedId(null)
      return
    }
    if (!propertyId) {
      setError("Property context is missing, so generation cannot run.")
      return
    }
    void handleGenerate()
    // Only auto-generate when the drawer opens or the requested fix type changes.
    // Channel and season changes are intentionally manual via Regenerate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contentType, propertyId])

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      window.setTimeout(() => setCopiedId(null), 2000)
    } catch {
      setError("Copy failed. Try again.")
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <SheetTitle className="font-display text-xl">{typeLabels[contentType]}</SheetTitle>
              <SheetDescription className="text-[12px]">
                {typeDescriptions[contentType]}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mb-6 flex gap-3">
          <div className="flex-1">
            <label className="mb-1.5 block text-[11px] font-medium text-muted-foreground">
              Channel
            </label>
            <Select value={channel} onValueChange={(value) => setChannel(value as "airbnb" | "booking" | "direct")}>
              <SelectTrigger className="h-9 text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="airbnb">Airbnb</SelectItem>
                <SelectItem value="booking">Booking.com</SelectItem>
                <SelectItem value="direct">Direct booking</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {contentType === "seasonal" ? (
            <div className="flex-1">
              <label className="mb-1.5 block text-[11px] font-medium text-muted-foreground">
                Season
              </label>
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger className="h-9 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current ({currentSeason || "Active"})</SelectItem>
                  <SelectItem value="spring">Spring</SelectItem>
                  <SelectItem value="summer">Summer</SelectItem>
                  <SelectItem value="autumn">Autumn</SelectItem>
                  <SelectItem value="winter">Winter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        <div className="mb-6 rounded-2xl border border-border/40 bg-card/40 p-4">
          <p className="text-[12px] font-medium text-foreground">Generation context</p>
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
            Stayframe uses the live listing audit, review intelligence, season profile, house rules, places, and recent performance context to generate these fixes.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="outline" className="text-[10px]">Audit-backed</Badge>
            <Badge variant="outline" className="text-[10px]">Review-aware</Badge>
            {contentType === "seasonal" ? <Badge variant="outline" className="text-[10px]">Season-tuned</Badge> : null}
            {modelUsed ? <Badge variant="outline" className="text-[10px]">Model: {modelUsed}</Badge> : null}
          </div>
        </div>

        {generating ? (
          <div className="mb-6 rounded-2xl border border-border/40 bg-card/40 p-8 text-center">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            <p className="text-[13px] font-medium text-foreground">
              {loadingMessages[loadingMsg]}
            </p>
          </div>
        ) : null}

        {error ? (
          <div className="mb-6 rounded-2xl border border-destructive/20 bg-destructive/5 p-4 text-[12px] text-destructive">
            {error}
          </div>
        ) : null}

        {!generating && variants.length > 0 ? (
          <div className="space-y-4">
            {variants.map((variant) => (
              <div key={variant.id} className="rounded-2xl border border-border/40 bg-card/40 p-5">
                <p className="mb-3 whitespace-pre-line text-[13px] leading-relaxed text-foreground">
                  {variant.text}
                </p>

                <p className="mb-3 text-[11px] italic leading-relaxed text-muted-foreground/80">
                  {variant.rationale}
                </p>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {variant.sources.map((source) => (
                      <Badge
                        key={source}
                        variant="outline"
                        className="h-5 px-1.5 py-0 text-[9px] font-normal text-muted-foreground/70"
                      >
                        {source}
                      </Badge>
                    ))}
                    <span className="ml-1 text-[10px] text-muted-foreground/50">
                      {variant.charCount} chars
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleCopy(variant.id, variant.text)}
                    className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                  >
                    {copiedId === variant.id ? (
                      <>
                        <Check className="h-3 w-3" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-6">
          <Button
            variant={variants.length > 0 ? "outline" : "default"}
            onClick={() => void handleGenerate()}
            className="w-full text-[12px]"
            disabled={!propertyId || generating}
          >
            {generating ? "Generating…" : variants.length > 0 ? "Regenerate variants" : "Generate variants"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
